export interface Warehouse {
  id: string
  code: string
  name: string
  address: Record<string, string> | null
  is_active: boolean
  created_at: string
}

export interface Customer { id: string; code: string; name: string }

export interface ParsedGroup {
  id: string
  name: string
  order: number
}

export interface LoadingListItem {
  hbl_no: string
  job_ref: string | null
  split_no: string | null
  container_no: string | null
  client_ref: string
  customer_name: string
  description: string
  system_ref: string | null
  expected_cartons: number
  expected_weight_kg: number | null
  expected_cbm: number | null
  net_weight_kg: number | null
  delivery_address: string
  delivery_postcode: string
  delivery_contact: string
  delivery_method: string | null
  delivery_tracking_no: string | null
  shipment_id: string | null
  amazon_ref_id: string | null
  vat_number: string | null
  appointment_code: string | null
  physical_warehouse: string | null
  group_id: string | null
  notes: string
}

export interface LoadingList {
  id: string
  warehouse_id: string
  job_ref: string | null
  container_no: string | null
  source_customer_id: string | null
  file_url: string | null
  file_name: string | null
  file_type: string | null
  version: number
  uploaded_at: string
  uploaded_by: string | null
  parse_status: 'manual' | 'pending' | 'parsing' | 'parsed' | 'failed'
  parsed_data: { items: LoadingListItem[]; groups?: ParsedGroup[] } | null
  notes: string | null
  expected_total_cartons: number | null
  expected_total_weight_kg: number | null
  expected_total_cbm: number | null
  container_eta_at: string | null
  free_demurrage_days: number | null
  raw_columns: RawColumn[] | null
  column_mapping: Record<string, string> | null
  customer?: { code: string; name: string } | null
}

export interface RawColumn {
  index: number
  letter: string
  header: string
  samples: string[]
}

export interface DevanningPlan {
  id: string
  warehouse_id: string
  container_no: string
  job_ref: string | null
  vessel: string | null
  voyage: string | null
  carrier: string | null
  eta_date: string | null
  source_loading_list_id: string | null
  status: 'pending' | 'planned'
  planned_devan_at: string | null
  notes: string | null
  created_at: string
  last_reason: string | null
  last_changed_at: string | null
  last_changed_by: string | null
}

export interface DevanningPlanItem {
  id: string
  plan_id: string
  hbl_no: string | null
  client_ref: string | null
  customer_id: string | null
  description: string | null
  expected_cartons: number
  expected_weight_kg: number | null
  expected_cbm: number | null
  delivery_address: string | null
  delivery_postcode: string | null
  delivery_contact: string | null
  delivery_method: string | null
  // Set = picked from the pricing_delivery_methods lookup (clean path); null = free text in
  // delivery_method above, either historical data or the "Other" option. See
  // components/DeliveryMethodSelect.tsx.
  delivery_method_id: string | null
  split_no: string | null
  system_ref: string | null
  notes: string | null
  sort_order: number
  group_name: string | null
  customer?: { code: string; name: string } | null
}

export interface Devanning {
  id: string
  plan_id: string
  warehouse_id: string
  container_no: string
  arrival_date: string | null
  arrival_at: string | null
  devanning_started_at: string | null
  devanning_completed_at: string | null
  devanned_by: string | null
  haulier: string | null
  status: 'awaiting' | 'started' | 'completed' | 'cancelled'
  notes: string | null
  created_at: string
  devan_date_override: string | null
  devan_date_override_by: string | null
  devan_date_override_at: string | null
  pushed_to_inbound: boolean
  pushed_to_inbound_at: string | null
  pushed_to_inbound_by: string | null
  plan?: { container_no: string; job_ref: string | null; vessel: string | null; eta_date: string | null } | null
}

export interface DevanningItem {
  id: string
  devanning_id: string
  plan_item_id: string | null
  hbl_no: string | null
  description: string | null
  customer_id: string | null
  delivery_address: string | null
  expected_cartons: number
  expected_weight_kg: number | null
  expected_cbm: number | null
  actual_cartons: number
  actual_weight_kg: number | null
  actual_cbm: number | null
  pallet_count: number
  pallets_qty: number
  pallets_detail: { p?: number; pallet_no?: number; ctns: number }[] | null
  hold_flag: boolean
  palleting_details_note: string | null
  condition: 'good' | 'damaged' | 'wet' | 'short' | 'over'
  notes: string | null
  photos: string[]
  split_no: string | null
  system_ref: string | null
  delivery_method: string | null
  delivery_method_id: string | null
  delivery_tracking_no: string | null
  shipment_id: string | null
  amazon_ref_id: string | null
  group_name: string | null
  actual_cartons_received: number | null
  skip_pallet_detail: boolean | null
  manual_pallet_total: number | null
  manual_carton_sum: number | null
  plt_location: string | null
  customer?: { code: string; name: string } | null
  plan_item?: { client_ref: string | null } | null
}

// Z50.X (2nd rewrite): matches supabase/migrations/20260917_z50x2_delivery_collection_notes_rewrite.sql.
// A row only exists once a note has actually been generated -- see Devannings/Notes.tsx.
// COLLECTION used to be one generic type -- which courier/self/supplier picked up
// determines the Release To box content + title suffix on the same PDF template
// (DeliveryCollectionNotePDF.tsx), so it's now three note_type values.
export type DeliveryNoteType = 'DELIVERY' | 'COLLECTION_COURIER' | 'COLLECTION_SELF' | 'COLLECTION_SUPPLIER'

export interface DeliveryNote {
  id: string
  serial_no: number
  note_type: DeliveryNoteType
  delivery_method_id?: string | null
  // Destination postcode: parsed from deliver_to_addr/release_to_addr on save, hand-correctable (Billing basis panel)
  delivery_postcode?: string | null
  source_devanning_item_id?: string | null
  // null for HAULAGE notes (raised on the Job page, no devanning behind them)
  devanning_id: string | null
  note_source: 'WAREHOUSE' | 'HAULAGE'
  container_no: string | null
  job_id: string | null
  job_ref: string
  customer_ref: string | null
  collect_from_code: string | null
  collect_from_addr: string | null
  collect_from_customer_id: string | null
  collect_from_warehouse_id: string | null
  release_to_name: string
  release_to_addr: string | null
  release_to_phone: string | null
  release_to_customer_id: string | null
  release_to_warehouse_id: string | null
  scheduled_date: string
  scheduled_time: string | null
  marks_numbers: string[]
  description: string | null
  summary_text: string | null
  nop: number
  pallets_qty: number
  weight_kg: number | null
  cube_cbm: number | null
  remarks: string | null
  haulier: string | null
  haulier_company_id: string | null
  notes: string | null
  authorise_to: string | null
  vehicle_reg: string | null   // COLLECTION_SELF / COLLECTION_SUPPLIER only
  driver_name: string | null   // COLLECTION_SELF / COLLECTION_SUPPLIER only
  issued_by_user_id: string | null
  issued_by_name: string | null
  issued_at: string
  item_ids: string[]
  pdf_url: string | null
  status: 'ACTIVE' | 'SUPERSEDED' | 'VOID'
  superseded_by: string | null
  voided_at: string | null
  voided_by: string | null
  void_reason: string | null
  lifecycle_status: 'DRAFT' | 'PLANNED' | 'COMPLETED'
  planned_at: string | null
  planned_by: string | null
  completed_at: string | null
  completed_by: string | null
  // The "first leg confirmed" moment -- distinct from completed_at, which now means "the whole
  // job is finished" (COLLECTION_ONLY: same moment as completed_at; COLLECTION_AND_DELIVERY:
  // set when collected, completed_at only once delivery is also confirmed).
  collected_at: string | null
  collected_by: string | null
  readiness_status: 'PENDING_ARRIVAL' | 'PENDING_INBOUND' | 'READY' | null
  readiness_checked_at: string | null
  forced_completion: boolean
  forced_completion_reason: string | null
  overridden_fields: Record<string, boolean>
  fulfilment_type: 'COLLECTION_ONLY' | 'COLLECTION_AND_DELIVERY' | null
  fulfilment_type_overridden: boolean
  // Final consignee -- only used when fulfilment_type = 'COLLECTION_AND_DELIVERY'. Mirrors
  // release_to_* (which always means "who takes it from us", regardless of fulfilment_type).
  deliver_to_name: string | null
  deliver_to_addr: string | null
  deliver_to_phone: string | null
  deliver_to_customer_id: string | null
  actual_driver_name: string | null
  actual_vehicle_reg: string | null
  actual_receiver_name: string | null
  completion_notes: string | null
  pod_received: boolean
  collection_signed: boolean
  delivered_at: string | null
  delivered_by: string | null
  transit_status: 'PENDING' | 'IN_TRANSIT' | 'DELIVERED' | null
  created_at: string
  updated_at: string
}

export interface DevanningOutput {
  id: string
  devanning_id: string
  type: 'devanning_report' | 'collection_note' | 'delivery_note'
  delivery_address: string | null
  pdf_url: string | null
  generated_at: string
  generated_by: string | null
}

// Shared UI helpers
export const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'
export const btnPrimary = 'px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
export const btnSecondary = 'px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700'
export const btnGreen = 'px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'

export function fmtDate(d?: string | null) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB')
}

export function fmtDateTime(d?: string | null) {
  if (!d) return '—'
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
